import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate, fmtMoney, fmtNumber } from "@/lib/format";
import { toast } from "sonner";
import {
  Users, CreditCard, ScrollText, ShieldCheck, LogIn, Database, FileText,
  Building2, Flag, FlagOff, Check, ArrowRightLeft, X, Loader2, Download, Copy, Archive,
} from "lucide-react";

const TABS = [
  { id: "overview", label: "Overview", icon: Building2 },
  { id: "users", label: "Users", icon: Users },
  { id: "billing", label: "Billing", icon: CreditCard },
  { id: "audit", label: "Audit log", icon: ScrollText },
];

export default function TenantDetailDrawer({ tenantId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [impersonatingNow, setImpersonatingNow] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState(null); // {password, filename}
  const { startImpersonation } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    api
      .get(`/admin/tenants/${tenantId}/detail`)
      .then((r) => setData(r.data))
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load tenant"))
      .finally(() => setLoading(false));
  }, [tenantId]);

  const loginAs = async () => {
    setImpersonatingNow(true);
    try {
      const { data: imp } = await api.post(`/admin/tenants/${tenantId}/impersonate`);
      startImpersonation(imp.token, imp.user, imp.tenant);
      toast.success(`Support session opened for ${imp.tenant.name} · expires in ${imp.expires_in_minutes}m`);
      onClose?.();
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start support session");
    } finally {
      setImpersonatingNow(false);
    }
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/admin/tenants/${tenantId}/export`, { responseType: "blob" });
      const password = res.headers["x-export-password"];
      // Parse filename out of Content-Disposition; fall back to a sensible default
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = (match && match[1]) ||
        `financialsready-export-${t?.name || "tenant"}-${new Date().toISOString().slice(0, 10)}.zip`;
      // Trigger browser download
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportResult({ password, filename });
      toast.success("Archive downloaded · copy the password before closing");
    } catch (err) {
      // err.response.data is a Blob when we set responseType=blob, so read it
      let detail = "Export failed";
      try {
        if (err?.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          detail = JSON.parse(text)?.detail || detail;
        } else {
          detail = err?.response?.data?.detail || detail;
        }
      } catch { /* ignore */ }
      toast.error(detail);
    } finally {
      setExporting(false);
    }
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(exportResult?.password || "");
      toast.success("Password copied to clipboard");
    } catch {
      toast.error("Couldn't copy — select the text and copy manually");
    }
  };

  const t = data?.tenant;
  const counts = data?.counts || {};

  return (
    <Dialog open={!!tenantId} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-hidden flex flex-col p-0" data-testid="tenant-detail-drawer">
        <DialogHeader className="px-6 pt-6 pb-3 border-b" style={{ borderColor: "var(--ll-border)" }}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="font-display flex items-center gap-2">
                <Building2 size={16} />
                {t?.name || "Tenant"}
              </DialogTitle>
              <DialogDescription className="text-[var(--ll-ink-soft)] mt-1 truncate font-mono-fin text-[10px]">
                {t?.id}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={exportData}
                disabled={exporting || loading || !t}
                data-testid="export-tenant-btn"
                className="text-xs px-3 py-2 border bg-white hover:bg-[var(--ll-bg-sidebar)] flex items-center gap-1.5 disabled:opacity-60"
                style={{ borderColor: "var(--ll-border)", color: "var(--ll-ink)" }}
                title="Download AES-256 encrypted ZIP of every tenant record (GDPR / CCPA)"
              >
                {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                {exporting ? "Exporting…" : "Export data"}
              </button>
              <button
                type="button"
                onClick={loginAs}
                disabled={impersonatingNow || loading || !t}
                data-testid="login-as-tenant-btn"
                className="text-xs px-3 py-2 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] flex items-center gap-1.5 disabled:opacity-60"
                title="Mint a 15-min JWT scoped to this tenant's owner for support / debug"
              >
                {impersonatingNow ? <Loader2 size={12} className="animate-spin" /> : <LogIn size={12} />}
                {impersonatingNow ? "Opening…" : "Login as tenant"}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 -mb-3 overflow-x-auto">
            {TABS.map((tab_) => (
              <button
                key={tab_.id}
                type="button"
                onClick={() => setTab(tab_.id)}
                data-testid={`tab-${tab_.id}`}
                className={`px-3 py-2 text-xs flex items-center gap-1.5 border-b-2 transition-colors whitespace-nowrap ${tab === tab_.id ? "border-[var(--ll-ink)] text-[var(--ll-ink)] font-medium" : "border-transparent text-[var(--ll-ink-soft)] hover:text-[var(--ll-ink)]"}`}
              >
                <tab_.icon size={12} /> {tab_.label}
              </button>
            ))}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading && <div className="ll-progress" />}

          {/* Overview */}
          {!loading && tab === "overview" && t && (
            <div className="space-y-5" data-testid="tab-content-overview">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px" style={{ background: "var(--ll-border)" }}>
                <CountTile icon={Users} label="Users" value={fmtNumber(counts.users || 0)} />
                <CountTile icon={Database} label="Bank accts" value={fmtNumber(counts.bank_accounts || 0)} />
                <CountTile icon={FileText} label="Statements" value={fmtNumber(counts.statements || 0)} />
                <CountTile icon={ScrollText} label="Transactions" value={fmtNumber(counts.transactions || 0)} />
              </div>
              <KV k="Owner email">{t.owner_email || "—"}</KV>
              <KV k="Plan">{t.plan || "trial"}</KV>
              <KV k="Status">
                <span className="inline-flex items-center gap-1.5">
                  {t.subscription_status || "trial"}
                  {t.subscription_status === "suspended" && (
                    <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-widest"
                      style={{ background: "var(--ll-ochre-bg)", color: "var(--ll-ochre-ink)" }}>Suspended</span>
                  )}
                </span>
              </KV>
              <KV k="Bank account limit">{t.bank_account_limit ?? "—"}</KV>
              <KV k="Team members limit">{t.seats_limit ?? "—"}</KV>
              <KV k="Teller connections">{counts.bank_connections || 0}</KV>
              <KV k="Created">{fmtDate(t.created_at)}</KV>
              <KV k="Stripe customer">{t.stripe_customer_id || "—"}</KV>
              <KV k="Brand color">
                {t.brand_color ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="w-3 h-3 inline-block border" style={{ background: t.brand_color, borderColor: "var(--ll-border)" }} />
                    <span className="font-mono-fin text-xs">{t.brand_color}</span>
                  </span>
                ) : "—"}
              </KV>
            </div>
          )}

          {/* Users */}
          {!loading && tab === "users" && (
            <div data-testid="tab-content-users">
              {(data?.users || []).length === 0 ? (
                <Empty icon={Users} text="No users in this tenant." />
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] border-b" style={{ borderColor: "var(--ll-border)" }}>
                      <th className="py-2 font-medium">Name / Email</th>
                      <th className="py-2 font-medium">Role</th>
                      <th className="py-2 font-medium">Status</th>
                      <th className="py-2 font-medium">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.users.map((u) => (
                      <tr key={u.id} className="border-b" style={{ borderColor: "var(--ll-border)" }}>
                        <td className="py-2.5">
                          <div className="font-medium">{u.name}</div>
                          <div className="text-xs text-[var(--ll-ink-soft)]">{u.email}</div>
                        </td>
                        <td className="py-2.5 text-xs uppercase tracking-widest">
                          {u.role}
                          {u.is_platform_admin && (
                            <span className="ml-1.5 text-[9px] px-1.5 py-0.5 inline-flex items-center gap-1"
                              style={{ background: "var(--ll-mint)", color: "var(--ll-ink)" }}>
                              <ShieldCheck size={9} /> Platform admin
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 text-xs">
                          {u.is_active ? (
                            <span className="inline-flex items-center gap-1 text-[#059669]"><Check size={11} /> Active</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[var(--ll-ink-muted)]"><X size={11} /> Inactive</span>
                          )}
                        </td>
                        <td className="py-2.5 text-xs text-[var(--ll-ink-soft)]">{fmtDate(u.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Billing */}
          {!loading && tab === "billing" && (
            <div data-testid="tab-content-billing">
              {(data?.billing || []).length === 0 ? (
                <Empty icon={CreditCard} text="No billing transactions on file for this tenant." />
              ) : (
                <ul className="divide-y" style={{ borderColor: "var(--ll-border)" }}>
                  {data.billing.map((b) => (
                    <li key={b.id || b.session_id} className="py-3 flex items-center justify-between gap-3" style={{ borderColor: "var(--ll-border)" }}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{b.description || b.plan_id || "Billing event"}</div>
                        <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">
                          {b.payment_status || b.status || "—"} · {fmtDate(b.created_at)}
                        </div>
                      </div>
                      <div className="font-mono-fin text-sm shrink-0">{fmtMoney(b.amount_total || b.amount || 0)}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Audit */}
          {!loading && tab === "audit" && (
            <div data-testid="tab-content-audit">
              {(data?.audit || []).length === 0 && (data?.flag_events || []).length === 0 ? (
                <Empty icon={ScrollText} text="No audit history yet." />
              ) : (
                <ol className="relative border-l border-[var(--ll-border)] ml-2 space-y-3 pl-4">
                  {(data?.audit || []).map((ev) => (
                    <AuditRow key={ev.id} ev={ev} kind="admin" />
                  ))}
                  {(data?.flag_events || []).map((ev) => (
                    <AuditRow key={`f-${ev.id}`} ev={ev} kind="flag" />
                  ))}
                </ol>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CountTile({ icon: Icon, label, value }) {
  return (
    <div className="p-4 bg-white">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">{label}</div>
        <Icon size={12} className="opacity-60" />
      </div>
      <div className="font-mono-fin text-xl leading-none">{value}</div>
    </div>
  );
}

function KV({ k, children }) {
  return (
    <div className="grid grid-cols-[180px,1fr] gap-3 items-center border-b py-2" style={{ borderColor: "var(--ll-border)" }}>
      <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">{k}</span>
      <span className="text-sm">{children}</span>
    </div>
  );
}

function Empty({ icon: Icon, text }) {
  return (
    <div className="py-10 text-center text-sm text-[var(--ll-ink-soft)]">
      <Icon size={22} className="mx-auto mb-2 opacity-40" />
      {text}
    </div>
  );
}

function AuditRow({ ev, kind }) {
  let icon = ArrowRightLeft;
  let tint = "var(--ll-ink-soft)";
  let label = ev.action;
  let detail = null;

  if (kind === "flag") {
    icon = ev.action === "flagged" ? Flag : FlagOff;
    tint = ev.action === "flagged" ? "var(--ll-ochre)" : "var(--ll-ink-muted)";
    label = ev.action === "flagged" ? "Flagged for review" : "Flag removed";
    detail = ev.reason ? `"${ev.reason}"` : null;
  } else if (ev.action === "tenant.patch") {
    label = "Tenant updated";
    const fields = Object.keys(ev.payload?.after || {});
    if (fields.length) {
      detail = fields.map((k) => {
        const b = ev.payload?.before?.[k];
        const a = ev.payload?.after?.[k];
        return `${k}: ${b ?? "—"} → ${a ?? "—"}`;
      }).join(" · ");
    }
  } else if (ev.action === "tenant.impersonate") {
    label = "Support session opened";
    detail = `Logged in as ${ev.payload?.target_email || "tenant user"} · 15-min token`;
  }

  const Icon = icon;
  return (
    <li className="relative">
      <span
        className="absolute -left-[22px] top-0.5 w-3.5 h-3.5 grid place-items-center text-white"
        style={{ background: tint }}
      >
        <Icon size={8} strokeWidth={2.5} />
      </span>
      <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] font-mono-fin">
        {fmtDate(ev.created_at?.slice(0, 10))} · {(ev.created_at || "").slice(11, 16)} UTC
      </div>
      <div className="text-sm mt-0.5">
        <span className="font-medium">{ev.actor_name || ev.actor_email || "Unknown"}</span>{" "}
        <span className="text-[var(--ll-ink-soft)]">· {label}</span>
      </div>
      {detail && (
        <div className="mt-1 text-xs text-[var(--ll-ink-soft)] font-mono-fin border-l-2 pl-2"
          style={{ borderColor: "var(--ll-border)" }}>
          {detail}
        </div>
      )}
    </li>
  );
}
